import { inject, Injectable, signal, computed } from '@angular/core';
import { Database } from '../services/database';
import { AppointmentSession, AppointmentStatus, Company, User, JobApplicationStatus } from '../models/database.types';
import { take } from 'rxjs';
import { Supabase } from '../services/supabase';
import { AuthUserInfo } from '../models/auth.types';
import { RealtimeChannel } from '@supabase/supabase-js';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root'
})
export class AppState {
  private readonly db = inject(Database);
  private readonly supabase = inject(Supabase);
  private messageService = inject(MessageService);
  private translateService = inject(TranslateService);
  private sessionsChannel: RealtimeChannel | null = null;
  private lastToastSessionId: string | null = null;
  private lastToastTimestamp = 0;

  readonly user = signal<User | null>(null);
  readonly company = signal<Company | null>(null);
  readonly sessions = signal<AppointmentSession[]>([]);
  readonly userCount = signal<number>(0);
  readonly authUserCount = signal<number>(0);
  readonly verifiedAuthUsers = signal<AuthUserInfo[]>([]);
  readonly pendingAuthUsers = signal<AuthUserInfo[]>([]);
  readonly partnerContactCount = signal<number>(0);
  readonly pendingApplicationsCount = signal<number>(0);

  readonly isInitialized = signal(false);
  readonly loading = signal(false);

  constructor() {
    // Listen to Supabase loading events
    if (typeof window !== 'undefined') {
      window.addEventListener('supabase-loading-change', ((event: CustomEvent) => {
        this.loading.set(event.detail.loading);
      }) as EventListener);
    }
  }

  // Computed signals for filtered sessions
  readonly openSessions = computed(() => {
    const filtered = this.sessions().filter(s => 
      s.status === AppointmentStatus.WaitingForProposer ||
      s.status === AppointmentStatus.WaitingForSelector ||
      s.status === AppointmentStatus.Rejected
    );
    
    // Sort: First WaitingForParticipant, then Rejected, then WaitingForPartner
    // Within each group, sort by created_at (earliest first)
    return filtered.sort((a, b) => {
      // First sort by status priority
      if (a.status !== b.status) {
        if (a.status === AppointmentStatus.WaitingForSelector) return -1;
        if (b.status === AppointmentStatus.WaitingForSelector) return 1;
        if (a.status === AppointmentStatus.Rejected) return -1;
        if (b.status === AppointmentStatus.Rejected) return 1;
      }
      // Then sort by created_at (earliest first)
      return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
    });
  });

  readonly confirmedSessions = computed(() => {
    const now = new Date();
    return this.sessions().filter(s => 
      s.status === AppointmentStatus.Confirmed && 
      s.end_time && 
      new Date(s.end_time) > now
    );
  });

  public async loadInitialData(userId: string): Promise<void> {
    try {
      const user = await this.db.getUserById(userId).pipe(take(1)).toPromise();
      if (!user) throw new Error('User not found');
      this.user.set(user);

      if (!user.company_id) throw new Error('User has no company');
      const company = await this.db.getCompanyById(user.company_id).pipe(take(1)).toPromise();
      if (!company) throw new Error('Company not found');
      this.company.set(company);

      // Load only open and confirmed appointment sessions (not past confirmed)
      const sessions = await this.db.getOpenAndConfirmedAppointmentSessionsByUser(userId).pipe(take(1)).toPromise();
      this.sessions.set(sessions || []);

      // Setup Realtime subscription for appointment sessions
      this.setupSessionsRealtime(userId);

      // Load counts for subscription validation
      await this.loadCounts(user.company_id);

      // Load pending applications count
      await this.loadPendingApplicationsCount(user.company_id);

      // Setup job applications realtime subscription
      this.setupJobApplicationsRealtime(user.company_id);

      this.isInitialized.set(true);
    } catch (error) {
      console.error('Error loading initial data:', error);
    }
  }

  private setupSessionsRealtime(userId: string): void {
    // Cleanup existing channel if any
    if (this.sessionsChannel) {
      this.supabase.getClient().removeChannel(this.sessionsChannel);
    }

    // Create new channel for appointment_sessions UPDATE events
    this.sessionsChannel = this.supabase.getClient()
      .channel('appointment_sessions_updates')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'appointment_sessions',
          filter: `organizer_id=eq.${userId}`
        },
        (payload) => {
          const oldStatus = payload.old['status'] as AppointmentStatus;
          const newStatus = payload.new['status'] as AppointmentStatus;

          // Update session in local state
          const currentSessions = this.sessions();
          this.sessions.set(
            currentSessions.map(s =>
              s.id === payload.new['id'] ? payload.new as AppointmentSession : s
            )
          );

          // Show toast notification for relevant status changes
          this.showStatusChangeToast(oldStatus, newStatus, payload.new['id'] as string);

          // Trigger activity log reload (new log entry was created)
          // Add small delay to allow database to persist the new log entry
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('activity-log-updated'));
          }, 500);
        }
      )
      .subscribe();
  }

  private jobApplicationsChannel: RealtimeChannel | null = null;

  private setupJobApplicationsRealtime(companyId: string): void {
    // Cleanup existing channel if any
    if (this.jobApplicationsChannel) {
      this.supabase.getClient().removeChannel(this.jobApplicationsChannel);
    }

    // Create new channel for job_applications UPDATE events
    this.jobApplicationsChannel = this.supabase.getClient()
      .channel('job_applications_updates')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'job_applications',
          filter: `company_id=eq.${companyId}`
        },
        (payload) => {
          const newStatus = payload.new['status'] as JobApplicationStatus;

          // Update pending applications count based on new status
          if (newStatus === JobApplicationStatus.PendingReview) {
            this.pendingApplicationsCount.set(this.pendingApplicationsCount() + 1);
          } else {
            this.pendingApplicationsCount.set(Math.max(0, this.pendingApplicationsCount() - 1));
          }

          // Trigger job applications list reload
          window.dispatchEvent(new CustomEvent('job-applications-updated'));
        }
      )
      .subscribe();
  }

  private showStatusChangeToast(oldStatus: AppointmentStatus, newStatus: AppointmentStatus, sessionId: string): void {
    // Debounce: Prevent duplicate toasts for the same session within 5 seconds
    const now = Date.now();
    if (this.lastToastSessionId === sessionId && (now - this.lastToastTimestamp) < 5000) {
      return;
    }

    this.lastToastSessionId = sessionId;
    this.lastToastTimestamp = now;

    let messageKey = '';
    let severity: 'success' | 'warn' | 'info' = 'info';

    switch (newStatus) {
      case AppointmentStatus.Confirmed:
        messageKey = 'realtime.statusChange.confirmed';
        severity = 'success';
        break;
      case AppointmentStatus.Rejected:
        messageKey = 'realtime.statusChange.rejected';
        severity = 'warn';
        break;
      case AppointmentStatus.WaitingForSelector:
        messageKey = 'realtime.statusChange.proposalsSubmitted';
        severity = 'info';
        break;
      default:
        return; // Keine Toast für andere Änderungen
    }

    this.messageService.add({
      severity,
      summary: this.translateService.instant('realtime.statusChange.summary'),
      detail: this.translateService.instant(messageKey),
      life: 0, // 0 = sticky, verschwindet nicht automatisch
      sticky: true
    });
  }

  public async refreshSessions(): Promise<void> {
    const userId = this.user()?.id;
    if (!userId) return;

    try {
      // Load only open and confirmed sessions (not past confirmed)
      const sessions = await this.db.getOpenAndConfirmedAppointmentSessionsByUser(userId).pipe(take(1)).toPromise();
      this.sessions.set(sessions || []);
    } catch (error) {
      console.error('Error refreshing sessions:', error);
    }
  }

  private async loadCounts(companyId: string): Promise<void> {
    try {
      const [users, contacts] = await Promise.all([
        this.db.getUsersByCompany(companyId).pipe(take(1)).toPromise(),
        this.db.getContactsByCompany(companyId).pipe(take(1)).toPromise(),
        this.loadAuthUsers()
      ]);
      this.userCount.set(users?.length || 0);
      this.partnerContactCount.set(contacts?.length || 0);
    } catch (error) {
      console.error('Error loading counts:', error);
    }
  }

  private async loadPendingApplicationsCount(companyId: string): Promise<void> {
    try {
      const applications = await this.db.getPendingJobApplications(companyId).pipe(take(1)).toPromise();
      this.pendingApplicationsCount.set(applications?.length || 0);
    } catch (error) {
      console.error('Error loading pending applications count:', error);
      this.pendingApplicationsCount.set(0);
    }
  }

  private async loadAuthUsers(): Promise<void> {
    try {
      const result = await this.supabase.invokeFunction<{
        totalAuthUsers: number;
        verifiedUsers: AuthUserInfo[];
        pendingUsers: AuthUserInfo[];
      }>('get-auth-users', {}).pipe(take(1)).toPromise();

      if (result) {
        // +1 for the initial user who doesn't have company_id in metadata
        this.authUserCount.set((result.totalAuthUsers || 0) + 1);
        this.verifiedAuthUsers.set(result.verifiedUsers || []);
        this.pendingAuthUsers.set(result.pendingUsers || []);
      }
    } catch (error) {
      console.error('Error loading auth users:', error);
    }
  }

  public async refreshAuthUsers(): Promise<void> {
    await this.loadAuthUsers();
  }

  public async refreshCounts(): Promise<void> {
    const companyId = this.company()?.id;
    if (!companyId) return;
    await this.loadCounts(companyId);
  }

  public async refreshCompany(): Promise<void> {
    const companyId = this.company()?.id;
    if (!companyId) return;

    try {
      const company = await this.db.getCompanyById(companyId).pipe(take(1)).toPromise();
      if (company) {
        this.company.set(company);
      }
    } catch (error) {
      console.error('Error refreshing company:', error);
    }
  }

  public clear(): void {
    // Cleanup realtime channels
    if (this.sessionsChannel) {
      this.supabase.getClient().removeChannel(this.sessionsChannel);
      this.sessionsChannel = null;
    }
    if (this.jobApplicationsChannel) {
      this.supabase.getClient().removeChannel(this.jobApplicationsChannel);
      this.jobApplicationsChannel = null;
    }
    this.user.set(null);
    this.company.set(null);
    this.sessions.set([]);
    this.userCount.set(0);
    this.authUserCount.set(0);
    this.verifiedAuthUsers.set([]);
    this.pendingAuthUsers.set([]);
    this.partnerContactCount.set(0);
    this.pendingApplicationsCount.set(0);
    this.isInitialized.set(false);
  }
}
